import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, FileText, Mail, Loader2, Reply, Check, X } from "lucide-react";
import { useLang, useAuth } from "@/App";
import { Reveal, Overline } from "@/components/motion";

const API = process.env.REACT_APP_BACKEND_URL;

const thCls = "pb-3 pr-4 font-semibold";
const tdCls = "py-3 pr-4 align-top font-mono text-[11px] text-ink/75";
const tdNameCls = "py-3 pr-4 align-top text-xs font-semibold text-ink";

const STATUS_STYLES = {
  pending: "border-amber-300 bg-amber-50 text-amber-600",
  accepted: "border-teal/50 bg-teal/10 text-teal",
  rejected: "border-red-300 bg-red-50 text-red-500",
};

function fmtDate(iso, lang) {
  try {
    return new Date(iso).toLocaleString(lang, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function ReplyLink({ email, subject, quote, label, testid }) {
  const body = quote ? `\n\n---\n${String(quote).slice(0, 500)}` : "";
  return (
    <a
      data-testid={testid}
      href={`mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-teal/40 px-3 py-1.5 font-mono text-[10px] font-bold text-teal transition-colors duration-200 hover:bg-teal hover:text-white"
    >
      <Reply className="h-3 w-3" />
      {label}
    </a>
  );
}

export default function Admin() {
  const { t, lang } = useLang();
  const { user, checked } = useAuth();
  const a = t.admin;
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [tab, setTab] = useState("licenses");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (!checked) return;
    setStatus("loading");
    fetch(`${API}/api/admin/submissions`, { credentials: "include" })
      .then(async (r) => {
        if (r.ok) {
          setData(await r.json());
          setStatus("ok");
          fetch(`${API}/api/admin/mark-read`, { method: "POST", credentials: "include" })
            .then(() => window.dispatchEvent(new Event("dekrbat:read")))
            .catch(() => {});
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => setStatus("forbidden"));
  }, [checked, user]);

  const setLicenseStatus = async (id, status) => {
    try {
      const r = await fetch(`${API}/api/admin/license-requests/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!r.ok) throw new Error("status failed");
      const r2 = await fetch(`${API}/api/admin/submissions`, { credentials: "include" });
      if (r2.ok) setData(await r2.json());
    } catch {}
  };

  return (
    <section data-testid="admin-page" className="emerald-mesh grain-overlay relative min-h-[70vh] overflow-hidden pb-24 pt-20 sm:pt-28">
      <div className="grid-pattern absolute inset-0" aria-hidden="true" />
      <div className="hero-glow absolute inset-0" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Overline>Dekrbat Pure</Overline>
          <h1 className="mt-4 flex items-center gap-3 font-headline text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">
            <ShieldCheck className="h-9 w-9 text-teal" />
            {a.title}
          </h1>
        </Reveal>

        {status === "loading" && (
          <p className="mt-10 flex items-center gap-2 font-mono text-sm text-ink/50">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t.contact.drawer.loading}
          </p>
        )}

        {status === "forbidden" && (
          <div data-testid="admin-forbidden" className="mt-10 max-w-lg rounded-2xl border border-lined bg-white/85 p-8 text-center shadow-xl shadow-sky-900/5 backdrop-blur-md">
            <ShieldCheck className="mx-auto h-8 w-8 text-ink/30" />
            <p className="mt-4 font-headline text-lg font-bold text-ink">{user ? a.forbidden : a.loginFirst}</p>
            <Link
              data-testid="admin-forbidden-home-btn"
              to="/"
              className="mt-6 inline-flex items-center rounded-full bg-teal px-6 py-3 font-headline text-sm font-bold text-white transition-colors duration-300 hover:bg-deep"
            >
              {t.nav.home}
            </Link>
          </div>
        )}

        {status === "ok" && data && (() => {
          const pendingCount = data.license_requests.filter((r) => !r.status || r.status === "pending").length;
          const filteredLicenses =
            statusFilter === "all" ? data.license_requests : data.license_requests.filter((r) => (r.status || "pending") === statusFilter);
          const tabBtn = (key) =>
            `inline-flex items-center gap-2 rounded-full border px-5 py-2.5 font-headline text-xs font-bold transition-colors duration-300 ${
              tab === key ? "border-teal bg-teal text-white shadow-lg shadow-teal/25" : "border-lined bg-white/85 text-ink/60 hover:text-teal"
            }`;
          const countPill = (key, n) => (
            <span className={`rounded-full px-2 py-0.5 font-mono text-[10px] ${tab === key ? "bg-white/20 text-white" : "bg-teal/10 text-teal"}`}>{n}</span>
          );
          return (
          <div className="mt-10">
            <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Admin sections">
              <button
                role="tab"
                aria-selected={tab === "licenses"}
                data-testid="admin-tab-licenses"
                onClick={() => setTab("licenses")}
                className={tabBtn("licenses")}
              >
                <FileText className="h-3.5 w-3.5" />
                {a.licenses}
                {countPill("licenses", data.license_requests.length)}
                {pendingCount > 0 && (
                  <span data-testid="admin-tab-licenses-pending" className="rounded-full bg-amber-400 px-2 py-0.5 font-mono text-[10px] font-bold text-white">
                    {pendingCount}
                  </span>
                )}
              </button>
              <button
                role="tab"
                aria-selected={tab === "messages"}
                data-testid="admin-tab-messages"
                onClick={() => setTab("messages")}
                className={tabBtn("messages")}
              >
                <Mail className="h-3.5 w-3.5" />
                {a.contacts}
                {countPill("messages", data.contact_messages.length)}
              </button>
            </div>
          <div className="mt-6 grid grid-cols-1 gap-10">
            {tab === "licenses" && (
            <Reveal>
              <div className="rounded-3xl border border-lined bg-white/85 p-7 shadow-xl shadow-sky-900/5 backdrop-blur-md sm:p-9">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-teal" />
                  <h2 className="font-headline text-lg font-bold text-ink">{a.licenses}</h2>
                  <span className="rounded-full bg-teal/10 px-2.5 py-0.5 font-mono text-[10px] font-bold text-teal">
                    {filteredLicenses.length}
                  </span>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5" data-testid="admin-license-filters">
                  {["all", "pending", "accepted", "rejected"].map((f) => {
                    const n = f === "all" ? data.license_requests.length : data.license_requests.filter((r) => (r.status || "pending") === f).length;
                    return (
                      <button
                        key={f}
                        data-testid={`admin-filter-${f}`}
                        onClick={() => setStatusFilter(f)}
                        aria-pressed={statusFilter === f}
                        className={`rounded-full border px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.1em] transition-colors duration-200 ${
                          statusFilter === f ? "border-sky-600 bg-sky-600 text-white" : "border-lined bg-white text-ink/50 hover:text-teal"
                        }`}
                      >
                        {f === "all" ? a.filterAll : a[f]} · {n}
                      </button>
                    );
                  })}
                </div>
                {filteredLicenses.length === 0 ? (
                  <p data-testid="admin-licenses-empty" className="mt-6 font-mono text-xs text-ink/45">{a.empty}</p>
                ) : (
                  <div className="mt-6 overflow-x-auto">
                    <table data-testid="admin-licenses-table" className="w-full min-w-[900px] text-left">
                      <thead>
                        <tr className="border-b border-lined font-mono text-[10px] uppercase tracking-[0.16em] text-ink/45">
                          <th className={thCls}>{a.date}</th>
                          <th className={thCls}>{t.licensing.form.name}</th>
                          <th className={thCls}>{t.licensing.form.org}</th>
                          <th className={thCls}>{t.licensing.form.email}</th>
                          <th className={thCls}>{t.licensing.form.phone}</th>
                          <th className={thCls}>{t.licensing.form.department}</th>
                          <th className={thCls}>{t.licensing.form.type}</th>
                          <th className={thCls}>{a.statusT}</th>
                          <th className={thCls}>{t.licensing.form.ponds}</th>
                          <th className={thCls}>{t.licensing.form.message}</th>
                          <th className={thCls} />
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLicenses.map((r) => (
                          <tr key={r._id} data-testid={`admin-license-row-${r._id}`} className="border-b border-lined/70">
                            <td className={tdCls}>{fmtDate(r.created_at, lang)}</td>
                            <td className={tdNameCls}>{r.full_name}</td>
                            <td className={tdCls}>{r.organization}</td>
                            <td className={tdCls}>{r.email}</td>
                            <td className={tdCls}>{r.phone}</td>
                            <td className={tdCls}>{r.department}</td>
                            <td className={tdCls}>{r.license_type}</td>
                            <td className="py-3 pr-4 align-top">
                              <span
                                data-testid={`admin-license-status-${r._id}`}
                                className={`inline-flex rounded-full border px-2.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.12em] ${STATUS_STYLES[r.status || "pending"]}`}
                              >
                                {a[r.status || "pending"]}
                              </span>
                              <div className="mt-1.5 flex gap-1">
                                <button
                                  data-testid={`admin-license-accept-${r._id}`}
                                  onClick={() => setLicenseStatus(r._id, "accepted")}
                                  className="rounded-full border border-teal/40 p-1 text-teal transition-colors duration-200 hover:bg-teal hover:text-white"
                                  aria-label={a.accept}
                                  title={a.accept}
                                >
                                  <Check className="h-3 w-3" />
                                </button>
                                <button
                                  data-testid={`admin-license-reject-${r._id}`}
                                  onClick={() => setLicenseStatus(r._id, "rejected")}
                                  className="rounded-full border border-red-300 p-1 text-red-400 transition-colors duration-200 hover:bg-red-500 hover:text-white"
                                  aria-label={a.reject}
                                  title={a.reject}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            </td>
                            <td className={tdCls}>{r.pond_count}</td>
                            <td className={`${tdCls} max-w-[220px]`}>{r.message || "-"}</td>
                            <td className="py-3 align-top">
                              <ReplyLink
                                testid={`admin-reply-license-${r._id}`}
                                email={r.email}
                                subject={`Dekrbat Pure — ${r.license_type || "licencia"}`}
                                quote={r.message}
                                label={a.reply}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Reveal>
            )}

            {tab === "messages" && (
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-lined bg-white/85 p-7 shadow-xl shadow-sky-900/5 backdrop-blur-md sm:p-9">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-teal" />
                  <h2 className="font-headline text-lg font-bold text-ink">{a.contacts}</h2>
                  <span className="rounded-full bg-teal/10 px-2.5 py-0.5 font-mono text-[10px] font-bold text-teal">
                    {data.contact_messages.length}
                  </span>
                </div>
                {data.contact_messages.length === 0 ? (
                  <p data-testid="admin-contacts-empty" className="mt-6 font-mono text-xs text-ink/45">{a.empty}</p>
                ) : (
                  <div className="mt-6 overflow-x-auto">
                    <table data-testid="admin-contacts-table" className="w-full min-w-[680px] text-left">
                      <thead>
                        <tr className="border-b border-lined font-mono text-[10px] uppercase tracking-[0.16em] text-ink/45">
                          <th className={thCls}>{a.date}</th>
                          <th className={thCls}>{t.contact.form.name}</th>
                          <th className={thCls}>{t.contact.form.email}</th>
                          <th className={thCls}>{t.contact.form.subject}</th>
                          <th className={thCls}>{t.contact.form.message}</th>
                          <th className={thCls} />
                        </tr>
                      </thead>
                      <tbody>
                        {data.contact_messages.map((r) => (
                          <tr key={r._id} data-testid={`admin-contact-row-${r._id}`} className="border-b border-lined/70">
                            <td className={tdCls}>{fmtDate(r.created_at, lang)}</td>
                            <td className={tdNameCls}>{r.full_name}</td>
                            <td className={tdCls}>{r.email}</td>
                            <td className={tdCls}>{r.subject}</td>
                            <td className={`${tdCls} max-w-[320px]`}>{r.message}</td>
                            <td className="py-3 align-top">
                              <ReplyLink
                                testid={`admin-reply-contact-${r._id}`}
                                email={r.email}
                                subject={`Re: ${r.subject}`}
                                quote={r.message}
                                label={a.reply}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Reveal>
            )}
          </div>
          </div>
          );
        })()}
      </div>
    </section>
  );
}
